/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  HiOutlineClock,
  HiOutlineCollection,
  HiOutlineSearch,
  HiOutlineSparkles,
} from "react-icons/hi";
import { HiArrowUpRight } from "react-icons/hi2";

import { MdOutlineCampaign } from "react-icons/md";

import Screen from "../../layout/Screen";
import { BreadCrumbComponent } from "../../ui/BreadCrumbComponent";
import {
  getOneCountry,
  getOneLevel,
} from "../../lib/breadCrumb/BreadCrumb";
import { useAdminContext } from "../../context/admin/AdminContext";
import { cn } from "../../ui";
import CategoryTabs from "../dashboard/CategoryTabs";
import {
  CATEGORY_META,
  CATEGORY_ORDER,
  MODULES,
} from "../dashboard/modules";
import type {
  ModuleCategory,
  ModuleItem,
} from "../dashboard/types";
import { getType } from "../contentStats/helper";
import { _TYPES_ } from "./utils/types";

// Advertisement isn't part of the shared dashboard modules because it isn't
// surfaced in the global stats, but it belongs on the DoSee content hub for
// super admins — matching the legacy page's behaviour.
const EXTRA_MODULES: ModuleItem[] = [
  {
    category: "organizations",
    name: "Advertisements",
    route: "/data/advertisement",
    statKey: "advertisement",
    typeKey: _TYPES_.ADV,
    icon: <MdOutlineCampaign className="h-5 w-5" />,
    roles: ["admin", "super_admin"] as any,
  },
];

type Filter = ModuleCategory | "all";

const RECENT_STORAGE_KEY = "dosee.data.recent";
const RECENT_LIMIT = 4;

const readRecents = (): string[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, RECENT_LIMIT) : [];
  } catch {
    return [];
  }
};

const writeRecents = (names: string[]) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      RECENT_STORAGE_KEY,
      JSON.stringify(names.slice(0, RECENT_LIMIT)),
    );
  } catch {
    /* storage unavailable — ignore. */
  }
};

type ContentCardProps = {
  module: ModuleItem;
  compact?: boolean;
  onOpen: (m: ModuleItem) => void;
};

const ContentCard: React.FC<ContentCardProps> = ({ module, compact, onOpen }) => (
  <Link
    to={module.route}
    onClick={() => onOpen(module)}
    className={cn(
      "group relative flex overflow-hidden rounded-2xl border border-gray-200 bg-white transition-all",
      "hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-[0_10px_30px_-12px_rgba(0,145,145,0.25)]",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/25",
      compact ? "p-3" : "min-h-[120px] flex-col justify-between p-4",
    )}
  >
    <span
      aria-hidden
      className="pointer-events-none absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-brand transition-transform duration-300 ease-out group-hover:scale-x-100"
    />

    <div className={cn("flex gap-3", compact ? "items-center" : "items-start justify-between")}>
      <span
        aria-hidden
        className={cn(
          "flex shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand ring-1 ring-inset ring-brand/15 transition-colors",
          "group-hover:bg-brand group-hover:text-white group-hover:ring-brand",
          compact ? "h-9 w-9" : "h-10 w-10",
        )}
      >
        {module.icon}
      </span>

      {compact ? (
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold text-gray-900">
            {module.name}
          </span>
          <span className="block text-[11px] text-gray-500">Open section</span>
        </span>
      ) : (
        <span
          aria-hidden
          className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 opacity-0 transition-all duration-300 group-hover:border-brand/30 group-hover:bg-brand/10 group-hover:text-brand group-hover:opacity-100"
        >
          <HiArrowUpRight className="h-3.5 w-3.5" />
        </span>
      )}
    </div>

    {!compact ? (
      <div className="mt-4 min-w-0">
        <p className="truncate text-[14px] font-semibold leading-tight tracking-tight text-gray-900">
          {module.name}
        </p>
        <p className="mt-1 truncate text-[11.5px] text-gray-500">
          Manage {module.name.toLowerCase()}
        </p>
      </div>
    ) : null}
  </Link>
);

const HERO_ACCENT = "bg-brand/10 text-brand ring-1 ring-inset ring-brand/15";

const useRole = () => {
  const { sysAdmin } = useAdminContext();
  return (sysAdmin as any)?.role as string | undefined;
};

const useLocationLabel = () => {
  const level = (getOneLevel() as any[])?.[0]?.name as string | undefined;
  const country = (getOneCountry() as any[])?.[0]?.name as string | undefined;
  return level ?? country ?? "All content";
};

export const DataContent: React.FC = () => {
  const navigate = useNavigate();
  const { sysAdmin, setSingleAdmin } = useAdminContext();
  const role = useRole();
  const location = useLocationLabel();

  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [recents, setRecents] = useState<string[]>(() => readRecents());

  // Modules visible to this role — geography category is hidden since the
  // page is already scoped by location.
  const roleVisible = useMemo(
    () =>
      [...MODULES, ...EXTRA_MODULES].filter((m) => {
        if (m.category === "geography") return false;
        if (!m.roles) return true;
        return role ? m.roles.includes(role as any) : false;
      }),
    [role],
  );

  const visibleModules = useMemo(() => {
    const q = query.trim().toLowerCase();
    return roleVisible.filter((m) => {
      const categoryOk = filter === "all" || m.category === filter;
      const queryOk = !q || m.name.toLowerCase().includes(q);
      return categoryOk && queryOk;
    });
  }, [roleVisible, filter, query]);

  const categoryCounts = useMemo(
    () =>
      CATEGORY_ORDER.filter((c) => c !== "geography").map((category) => ({
        category,
        label: CATEGORY_META[category].title,
        count: roleVisible.filter((m) => m.category === category).length,
      })),
    [roleVisible],
  );

  const groupedByCategory = useMemo(() => {
    const buckets = new Map<ModuleCategory, ModuleItem[]>();
    for (const cat of CATEGORY_ORDER) buckets.set(cat, []);
    for (const m of visibleModules) buckets.get(m.category)?.push(m);
    return CATEGORY_ORDER.filter((c) => c !== "geography")
      .map((cat) => ({
        category: cat,
        meta: CATEGORY_META[cat],
        modules: buckets.get(cat) ?? [],
      }))
      .filter((b) => b.modules.length > 0);
  }, [visibleModules]);

  const recentModules = useMemo(() => {
    if (!recents.length) return [];
    const byName = new Map(roleVisible.map((m) => [m.name, m]));
    return recents
      .map((name) => byName.get(name))
      .filter((m): m is ModuleItem => Boolean(m));
  }, [recents, roleVisible]);

  const trackRecent = useCallback((module: ModuleItem) => {
    setRecents((prev) => {
      const next = [module.name, ...prev.filter((n) => n !== module.name)].slice(
        0,
        RECENT_LIMIT,
      );
      writeRecents(next);
      return next;
    });
  }, []);

  const openModule = useCallback(
    (module: ModuleItem) => {
      setSingleAdmin({
        ids: [(sysAdmin as any)?.admin_id],
        type: getType({ field: module.typeKey }),
        route: "/dosee/data",
      });
      trackRecent(module);
    },
    [setSingleAdmin, sysAdmin, trackRecent],
  );

  const quickOpen = () => {
    const top = visibleModules[0];
    if (!top) return;
    openModule(top);
    navigate(top.route);
  };

  // Keyboard: `/` focuses the search, `Escape` clears it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inField =
        target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA");
      if (!inField && e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === "Escape" && document.activeElement === searchRef.current) {
        setQuery("");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const totalSections = roleVisible.length;

  return (
    <Screen title={location}>
      <div className="relative h-full w-full overflow-y-auto overflow-x-hidden bg-gray-50">
        <div className="mx-auto w-full max-w-7xl space-y-8 px-4 py-6 sm:px-6">
          <BreadCrumbComponent />

          {/* ── Hero ───────────────────────────────────────────────── */}
          <section className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white">
            <div
              aria-hidden
              className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-brand/10 blur-3xl"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -bottom-20 right-32 h-40 w-40 rounded-full bg-brand/[0.07] blur-3xl"
            />

            <div className="relative grid gap-6 px-6 py-6 sm:px-8 sm:py-7 lg:grid-cols-[1.1fr_auto] lg:items-center lg:gap-10">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/5 px-3 py-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brand">
                  <span aria-hidden className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand/60" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand" />
                  </span>
                  Content Hub
                </div>

                <h1 className="mt-4 truncate text-[28px] font-semibold leading-tight tracking-tight text-gray-900 sm:text-[32px]">
                  {location}
                </h1>
                <p className="mt-2 text-[13.5px] text-gray-500">
                  Jump into any section to create, edit, or publish content.
                </p>
              </div>

              <div className={cn("flex items-center gap-3 rounded-2xl px-4 py-3.5", HERO_ACCENT)}>
                <span
                  aria-hidden
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand text-white"
                >
                  <HiOutlineCollection className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-[10.5px] font-semibold uppercase tracking-[0.1em] text-gray-500">
                    Sections
                  </p>
                  <p className="mt-0.5 truncate text-[20px] font-semibold tabular-nums leading-none tracking-tight text-gray-900">
                    {totalSections}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ── Filters + Search ────────────────────────────────── */}
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0 flex-1 overflow-x-auto">
              <CategoryTabs
                selected={filter}
                onChange={setFilter}
                total={totalSections}
                categories={categoryCounts}
              />
            </div>

            <div className="w-full lg:max-w-sm">
              <div className="relative">
                <HiOutlineSearch
                  aria-hidden
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  placeholder="Search sections…"
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      quickOpen();
                    }
                  }}
                  className="h-9 w-full rounded-xl border border-gray-200 bg-white pl-9 pr-16 text-[13px] text-gray-900 placeholder:text-gray-400 shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                  aria-label="Search sections"
                />
                <span
                  aria-hidden
                  className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500"
                >
                  /
                </span>
              </div>
            </div>
          </div>

          {/* ── Recently opened ───────────────────────────────── */}
          {recentModules.length > 0 ? (
            <section aria-label="Recently opened">
              <header className="mb-3 flex items-center gap-2.5">
                <span aria-hidden className="h-4 w-[3px] rounded-full bg-brand" />
                <h3 className="text-[15px] font-semibold tracking-tight text-gray-900">
                  Recently opened
                </h3>
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10.5px] font-medium text-gray-600">
                  <HiOutlineClock className="h-3 w-3" />
                  Quick access
                </span>
              </header>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {recentModules.map((module) => (
                  <ContentCard
                    key={`recent-${module.name}`}
                    module={module}
                    compact
                    onOpen={openModule}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {/* ── Sections by category ──────────────────────────── */}
          {groupedByCategory.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-14 text-center">
              <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-brand/10 text-brand">
                <HiOutlineSparkles className="h-4 w-4" />
              </div>
              <p className="mt-4 text-[13px] font-medium text-gray-900">
                No sections match {query ? `“${query}”` : "this filter"}.
              </p>
              <p className="mt-1 text-[12px] text-gray-500">
                Try another keyword or clear the filter.
              </p>
            </div>
          ) : (
            <div className="space-y-10">
              {groupedByCategory.map(({ category, meta, modules }) => (
                <section key={category} aria-label={meta.title}>
                  <header className="mb-4 flex items-end justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2.5">
                        <span
                          aria-hidden
                          className="h-4 w-[3px] rounded-full bg-brand"
                        />
                        <h3 className="truncate text-[16px] font-semibold tracking-tight text-gray-900">
                          {meta.title}
                        </h3>
                        <span className="inline-flex h-5 min-w-[1.5rem] items-center justify-center rounded-full bg-gray-100 px-1.5 text-[10.5px] font-semibold tabular-nums text-gray-600">
                          {modules.length}
                        </span>
                      </div>
                      <p className="mt-1 truncate pl-[14px] text-[12px] text-gray-500">
                        {meta.hint}
                      </p>
                    </div>
                  </header>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                    {modules.map((module) => (
                      <ContentCard
                        key={module.name}
                        module={module}
                        onOpen={openModule}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </Screen>
  );
};
